import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

import { login as apiLogin, logout as apiLogout, refreshAccessToken } from '../api/auth'
import { ApiError, setAccessToken, setTokenRefreshHandler } from '../api/client'
import { fetchMe } from '../api/me'
import type { AuthSession, OrganizationDto, UserRole } from '../api/types'
import { demoBuyerSession } from '../dev/buyerDemoData'
import { isDemoUiEnabled } from '../dev/demoMode'
import {
  DEMO_STAND_EMAIL,
  DEMO_STAND_PASSWORD,
  isDemoStandEnabled,
} from '../dev/demoStand'
import { clearSession, loadStoredSession, saveSession } from './sessionStorage'

type AuthContextValue = {
  session: AuthSession | null
  role: UserRole | null
  userRoles: string[]
  organizationName: string | null
  organizations: OrganizationDto[]
  loading: boolean
  login: (email: string, password: string, force?: boolean) => Promise<string>
  logout: () => void
  switchOrganization: (organizationId: number) => Promise<void>
  activateOrganization: (organization: OrganizationDto) => Promise<void>
  refreshOrganizations: () => Promise<void>
  updateProfileInSession: (patch: Partial<Pick<AuthSession, 'displayName' | 'email'>>) => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

const roleFromSession = (orgType: string | undefined): UserRole | null => {
  if (orgType === 'supplier') {
    return 'supplier'
  }
  if (orgType === 'buyer') {
    return 'buyer'
  }
  return null
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<AuthSession | null>(null)
  const [role, setRole] = useState<UserRole | null>(null)
  const [userRoles, setUserRoles] = useState<string[]>([])
  const [organizationName, setOrganizationName] = useState<string | null>(null)
  const [organizations, setOrganizations] = useState<OrganizationDto[]>([])
  const [loading, setLoading] = useState(true)

  const applyOrganization = useCallback((meOrganizations: OrganizationDto[], organizationId: number | null) => {
    setOrganizations(meOrganizations)
    if (organizationId == null) {
      setRole(null)
      setOrganizationName(null)
      return
    }
    const org = meOrganizations.find((item) => item.id === organizationId)
    setRole(roleFromSession(org?.org_type))
    setOrganizationName(org?.trade_name ?? org?.legal_name ?? null)
  }, [])

  const hydrateProfile = useCallback(async (nextSession: AuthSession): Promise<AuthSession> => {
    setAccessToken(nextSession.accessToken)
    const me = await fetchMe()
    setUserRoles(me.roles)
    applyOrganization(me.organizations, nextSession.organizationId)
    const synced: AuthSession = {
      ...nextSession,
      email: me.email,
      displayName: me.displayName,
    }
    saveSession(synced)
    setSession(synced)
    return synced
  }, [applyOrganization])

  useEffect(() => {
    setTokenRefreshHandler(async () => {
      const stored = loadStoredSession()
      if (!stored?.refreshToken) {
        return null
      }
      try {
        const tokens = await refreshAccessToken(stored.refreshToken)
        const nextSession: AuthSession = {
          ...stored,
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken ?? stored.refreshToken,
        }
        saveSession(nextSession)
        setAccessToken(tokens.accessToken)
        setSession(nextSession)
        return tokens.accessToken
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) {
          clearSession()
          setAccessToken(null)
          setSession(null)
          setRole(null)
          setUserRoles([])
          setOrganizationName(null)
        }
        return null
      }
    })
    return () => setTokenRefreshHandler(null)
  }, [])

  useEffect(() => {
    if (isDemoStandEnabled()) {
      void (async () => {
        try {
          const { session: nextSession } = await apiLogin(
            DEMO_STAND_EMAIL,
            DEMO_STAND_PASSWORD,
            true,
          )
          saveSession(nextSession)
          setAccessToken(nextSession.accessToken)
          const synced = await hydrateProfile(nextSession)
          setSession(synced)
        } catch (error) {
          console.error(
            'Не удалось открыть демо-кабинет',
            error instanceof Error ? error.message : 'Неизвестная ошибка',
          )
          clearSession()
          setAccessToken(null)
        } finally {
          setLoading(false)
        }
      })()
      return
    }

    const stored = loadStoredSession() ?? (isDemoUiEnabled ? demoBuyerSession() : null)
    if (!stored) {
      setLoading(false)
      return
    }

    void (async () => {
      try {
        setAccessToken(stored.accessToken)
        const synced = await hydrateProfile(stored)
        setSession(synced)
      } catch {
        if (isDemoUiEnabled) {
          try {
            const demo = demoBuyerSession()
            saveSession(demo)
            setAccessToken(demo.accessToken)
            const synced = await hydrateProfile(demo)
            setSession(synced)
          } catch {
            clearSession()
            setAccessToken(null)
          } finally {
            setLoading(false)
          }
          return
        }
        try {
          const tokens = await refreshAccessToken(stored.refreshToken)
          const nextSession: AuthSession = {
            ...stored,
            accessToken: tokens.accessToken,
            refreshToken: tokens.refreshToken ?? stored.refreshToken,
          }
          saveSession(nextSession)
          setAccessToken(tokens.accessToken)
          const synced = await hydrateProfile(nextSession)
          setSession(synced)
        } catch (error) {
          if (error instanceof ApiError && error.status === 401) {
            clearSession()
            setAccessToken(null)
          }
        }
      } finally {
        setLoading(false)
      }
    })()
  }, [hydrateProfile])

  const login = useCallback(
    async (email: string, password: string, force = false) => {
      const { session: nextSession, redirectPath } = await apiLogin(email, password, force)
      saveSession(nextSession)
      setAccessToken(nextSession.accessToken)
      const synced = await hydrateProfile(nextSession)
      setSession(synced)
      return redirectPath
    },
    [hydrateProfile],
  )

  const logout = useCallback(() => {
    void apiLogout(session)
    clearSession()
    setSession(null)
    setRole(null)
    setUserRoles([])
    setOrganizationName(null)
    setOrganizations([])
  }, [session])

  const switchOrganization = useCallback(
    async (organizationId: number) => {
      if (!session || session.organizationId === organizationId) {
        return
      }
      const nextSession: AuthSession = { ...session, organizationId }
      saveSession(nextSession)
      setSession(nextSession)
      applyOrganization(organizations, organizationId)
    },
    [session, organizations, applyOrganization],
  )

  const activateOrganization = useCallback(
    async (organization: OrganizationDto) => {
      if (!session) {
        return
      }
      const me = await fetchMe()
      const activeOrganization = me.organizations.find((item) => item.id === organization.id) ?? organization
      const nextSession: AuthSession = { ...session, organizationId: activeOrganization.id }
      saveSession(nextSession)
      setSession(nextSession)
      setUserRoles(me.roles)
      applyOrganization(
        me.organizations.some((item) => item.id === activeOrganization.id)
          ? me.organizations
          : [...me.organizations, activeOrganization],
        activeOrganization.id,
      )
    },
    [applyOrganization, session],
  )

  const refreshOrganizations = useCallback(async () => {
    if (!session) {
      return
    }
    const me = await fetchMe()
    applyOrganization(me.organizations, session.organizationId)
  }, [session, applyOrganization])

  const updateProfileInSession = useCallback(
    (patch: Partial<Pick<AuthSession, 'displayName' | 'email'>>) => {
      setSession((current) => {
        if (!current) {
          return current
        }
        const next = { ...current, ...patch }
        saveSession(next)
        return next
      })
    },
    [],
  )

  const value = useMemo(
    () => ({
      session,
      role,
      userRoles,
      organizationName,
      organizations,
      loading,
      login,
      logout,
      switchOrganization,
      activateOrganization,
      refreshOrganizations,
      updateProfileInSession,
    }),
    [
      session,
      role,
      userRoles,
      organizationName,
      organizations,
      loading,
      login,
      logout,
      switchOrganization,
      activateOrganization,
      refreshOrganizations,
      updateProfileInSession,
    ],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}
